import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SectionSkeleton from "@/components/base/SectionSkeleton";
import SEO from "@/components/SEO";
import ArticleCard from "@/components/base/ArticleCard";
import StoreCard from "@/components/base/StoreCard";
import {
  getTopicBySlug,
  getPostsByTopicSlug,
  getStoresByTopicSlug,
  getStoreFeaturesForTopic,
} from "@/api/content";
import { canonical, collectionPageSchema } from "@/utils/seo";
import type { Topic, PostCard, StoreCard } from "@/types/content";

const featureToQuestion: Record<string, string> = {
  "獨立包廂": "哪間有獨立包廂？",
  "淋浴設施": "哪間有淋浴設施？",
  "免費茶點": "哪間提供免費茶點？",
  "情侶雙人房": "哪間有情侶雙人房？",
  "線上預約": "哪間可以線上預約？",
  "植物染": "哪間使用植物染？",
  "日系設計": "哪間是日系設計？",
  "頭皮護理": "哪間有頭皮護理？",
  "無推銷": "哪間無推銷壓力？",
  "預約制": "哪間採預約制？",
  "韓國認證師傅": "哪間有韓國認證師傅？",
  "醫美級膠水": "哪間使用醫美級膠水？",
  "1週免費補睫": "哪間提供嫁接後免費補睫？",
  "30分鐘設計溝通": "哪間有充分的設計溝通？",
  "2個月免費補色": "哪間提供術後免費補色？",
  "試打方案": "哪間提供試打體驗？",
  "8人小班": "哪間採小班制教學？",
  "空中瑜珈": "哪間有空中瑜珈？",
  "皮拉提斯": "哪間有皮拉提斯？",
  "免費體驗課": "哪間有免費體驗課？",
  "專櫃級產品": "哪間使用專櫃級產品？",
  "妝前溝通充分": "哪間妝前溝通時間充足？",
  "新娘試妝": "哪間提供新娘試妝？",
  "中醫經絡背景": "哪間師傅有中醫背景？",
  "一次性罐具": "哪間使用一次性罐具？",
  "60塊draping布": "哪間 draping 測試布料最多？",
  "隨身色卡": "哪間提供隨身色卡？",
  "風格定位": "哪間提供風格定位諮詢？",
  "衣櫥整理": "哪間提供衣櫥整理服務？",
  "採購陪同": "哪間提供採購陪同？",
  "全方位改造": "哪間提供全方位形象改造？",
  "3個月跟進": "哪間有長期跟進服務？",
  "尼泊爾頌缽認證": "哪間引導師有尼泊爾認證？",
  "全隔音空間": "哪間是全隔音空間？",
  "冥想引導": "哪間有冥想引導課程？",
  "無刺激配方": "哪間使用無刺激配方？",
  "日本進口礦石板": "哪間使用日本進口礦石板？",
  "溫度精準控制": "哪間溫度控制精準？",
  "皮膚檢測": "哪間有皮膚檢測？",
  "客製化療程": "哪間提供客製化療程？",
  "療程後建議": "哪間提供療程後保養建議？",
};

export default function TopicDetailPage({ initialData }: { initialData?: Topic | null }) {
  const { slug } = useParams<{ slug: string }>();
  const [topic, setTopic] = useState<Topic | null>(initialData ?? null);
  const [posts, setPosts] = useState<PostCard[]>([]);
  const [storeCards, setStoreCards] = useState<StoreCard[]>([]);
  const [featureCounts, setFeatureCounts] = useState<Record<string, number>>();
  const [expandedFaq, setExpandedFaq] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(!initialData);

  useEffect(() => {
    if (!slug) return;
    getTopicBySlug(slug).then((data) => {
      if (data) {
        setTopic(data);
        Promise.all([
          getPostsByTopicSlug(slug),
          getStoresByTopicSlug(slug),
          getStoreFeaturesForTopic(slug),
        ]).then(([p, s, f]) => {
          setPosts(p);
          setStoreCards(s);
          setFeatureCounts(f);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });
  }, [slug]);

  const filterQA = useMemo(() => {
    const qa: { feature: string; question: string; stores: StoreCard[] }[] = [];
    const sortedFeatures = Object.entries(featureCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    for (const [feature] of sortedFeatures) {
      const question = featureToQuestion[feature] || `哪間有${feature}？`;
      const matchingStores = storeCards.filter((s) => s.features.includes(feature));
      if (matchingStores.length > 0) {
        qa.push({ feature, question, stores: matchingStores });
      }
    }
    return qa;
  }, [featureCounts, storeCards]);

  const compareStores = useMemo(() => {
    return [...storeCards].sort((a, b) => b.rating - a.rating);
  }, [storeCards]);

  const subTags = useMemo(() => {
    const tagCounts = new Map<string, number>();
    for (const p of posts) {
      for (const tag of p.tags) {
        tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
      }
    }
    return Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([tag]) => tag);
  }, [posts]);

  const toggleFaq = (feature: string) => {
    setExpandedFaq((prev) => {
      const next = new Set(prev);
      if (next.has(feature)) next.delete(feature);
      else next.add(feature);
      return next;
    });
  };

  if (loading) {
    return <SectionSkeleton type="spinner" />;
  }

  if (!topic) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          找不到此主題
        </h2>
        <a
          href="/topics"
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
        >
          返回主題列表
        </a>
      </div>
    );
  }

  const baseMeta = topic.seo || {
    title: `${topic.title}｜美好時光指南主題`,
    description: topic.description,
  };
  const meta = {
    ...baseMeta,
    canonical: baseMeta.canonical || canonical(`/topics/${topic.slug}`),
    keywords: baseMeta.keywords || [topic.title, "主題探索", "美好時光指南"],
    structuredData: baseMeta.structuredData || collectionPageSchema(baseMeta.title, baseMeta.description || topic.description, `/topics/${topic.slug}`),
  };

  return (
    <>
      <SEO meta={meta} />
      {/* Hero */}
      <section className="relative h-[360px] md:h-[480px] flex items-center justify-center overflow-hidden">
        <img
          src={topic.coverImage}
          alt={topic.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-3xl md:text-5xl font-bold mb-3">
            {topic.title}
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
            {topic.description}
          </p>
        </div>
      </section>

      {/* 子分類標籤 */}
      {subTags.length > 0 && (
        <section className="py-6 bg-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2">
              {subTags.map((tag) => (
                <a
                  key={tag}
                  href={`/tags/${encodeURIComponent(tag)}`}
                  className="px-4 py-2 bg-slate-50 text-slate-700 text-sm rounded-full border border-slate-200 hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                >
                  {tag}
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 篩選 QA */}
      {filterQA.length > 0 && (
        <section className="py-10 md:py-14 bg-surface-alt">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-questionnaire-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {topic.title} 快速篩選
              </h2>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 overflow-hidden card-shadow">
              {filterQA.map((qa, index) => (
                <div
                  key={qa.feature}
                  className={index > 0 ? "border-t border-slate-100" : ""}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(qa.feature)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <span className="font-medium text-slate-800">
                      {qa.question}
                    </span>
                    <div
                      className={`w-5 h-5 flex items-center justify-center text-slate-400 transition-transform ${
                        expandedFaq.has(qa.feature) ? "rotate-180" : ""
                      }`}
                    >
                      <i className="ri-arrow-down-s-line" />
                    </div>
                  </button>
                  {expandedFaq.has(qa.feature) && (
                    <div className="px-5 pb-4">
                      <div className="flex flex-wrap gap-2">
                        {qa.stores.map((s) => (
                          <a
                            key={s.id}
                            href={`/stores/${s.slug}`}
                            className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-full hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                          >
                            {s.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 店家對比總表 */}
      {compareStores.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-table-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {topic.title} 店家總覽
              </h2>
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-100 card-shadow">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="text-left px-4 py-3 font-medium">排名</th>
                    <th className="text-left px-4 py-3 font-medium">店家</th>
                    <th className="text-left px-4 py-3 font-medium">評分</th>
                    <th className="text-left px-4 py-3 font-medium">價格</th>
                    <th className="text-left px-4 py-3 font-medium">地址</th>
                    <th className="text-left px-4 py-3 font-medium">推薦語</th>
                    <th className="text-left px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {compareStores.map((store, index) => (
                    <tr
                      key={store.id}
                      className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {index < 3 ? (
                          <span
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                              index === 0
                                ? "bg-amber-100 text-amber-700"
                                : index === 1
                                  ? "bg-slate-200 text-slate-700"
                                  : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {index + 1}
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-500 text-xs font-bold">
                            {index + 1}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                        {store.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-amber-600 font-medium">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill" />
                          </div>
                          {store.rating}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {store.priceRange}
                      </td>
                      <td className="px-4 py-3 text-slate-500 max-w-[200px]">
                        <span className="truncate block">{store.location}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 max-w-[240px]">
                        <span className="truncate block">
                          {store.description.slice(0, 40)}...
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`/stores/${store.slug}`}
                          className="text-primary font-medium hover:text-primary-dark transition-colors whitespace-nowrap"
                        >
                          查看
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* 精選文章 */}
      {posts.length > 0 && (
        <section className="py-10 md:py-14 bg-surface-alt">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-article-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {topic.title} 精選文章
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {posts.map((post) => (
                <ArticleCard key={post.id} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 店家卡片 */}
      {storeCards.length > 0 && (
        <section className="py-10 md:py-14 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-6 h-6 flex items-center justify-center text-primary">
                <i className="ri-store-2-line" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">
                {topic.title} 推薦店家
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {storeCards.map((store) => (
                <StoreCard
                  key={store.id}
                  store={store}
                  footer={
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      <span className="line-clamp-1">{store.location}</span>
                    </div>
                  }
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-10 md:py-14 bg-surface-alt">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-3">
            還在找 {topic.title} 的好去處？
          </h2>
          <p className="text-slate-600 mb-6">
            瀏覽更多精選文章與真實評比，找到最適合你的選擇。
          </p>
          <a
            href="/articles"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
          >
            探索更多文章
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </a>
        </div>
      </section>
    </>
  );
}